import { User, Calculator, Calendar, TrendingUp, RefreshCw, Clock, CalendarRange, Zap } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { calculateSponsorshipProfit } from '../../types/multiInfluencer';
import type { CampaignStatus } from '../../types/campaign';
import { CAMPAIGN_STATUS_CONFIG } from '../../types/campaign';
import { FormBlock } from '../ui/FormBlock';

interface SponsorshipFormData {
  year: number;
  work_code: string;
  client: string;
  brand: string;
  campaign_ref: string;
  agent_name: string;
  sponsorship_amount: number;
  agency_commission_percent: number;
  entitlements: string;
  event_date: string;
  event_end_date?: string;
  campaign_status: CampaignStatus;
  sponsor_confirmed?: boolean;
  sponsor_confirmed_at?: string;
  admin_only?: boolean;
}

interface SponsorshipTabProps {
  formData: SponsorshipFormData;
  clients: string[];
  isEditing: boolean;
  agentName: string;
  onChange: (field: string, value: unknown) => void;
  onStatusChange?: (status: CampaignStatus) => void;
  statusSaving?: boolean;
}

export function SponsorshipTab({
  formData,
  clients,
  isEditing,
  agentName,
  onChange,
  onStatusChange,
  statusSaving,
}: SponsorshipTabProps) {
  const { t, ts } = useLanguage();
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    onChange(name, type === 'number' ? parseFloat(value) || 0 : value);
  };

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(amount);

  const profit = calculateSponsorshipProfit(
    formData.sponsorship_amount || 0,
    formData.agency_commission_percent || 0
  );

  const commissionDisplay = ((formData.agency_commission_percent || 0) * 100).toFixed(0);

  const getStatusStyles = (status: CampaignStatus) => {
    const config = CAMPAIGN_STATUS_CONFIG[status];
    if (!config) {
      return { bg: 'bg-gray-100', text: 'text-gray-800', combined: 'bg-gray-100 text-gray-800' };
    }
    return {
      bg: config.bgColor,
      text: config.textColor,
      combined: `${config.bgColor} ${config.textColor}`,
    };
  };

  const isMultiDayEvent = !!(formData.event_end_date && formData.event_end_date !== formData.event_date);

  const calculateEventDuration = () => {
    if (!formData.event_date) return null;
    if (!formData.event_end_date || formData.event_end_date === formData.event_date) return 1;

    const start = new Date(formData.event_date);
    const end = new Date(formData.event_end_date);
    const diffTime = Math.abs(end.getTime() - start.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    return diffDays;
  };

  const calculateDaysUntilEvent = () => {
    if (!formData.event_date) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const eventDate = new Date(formData.event_date);
    eventDate.setHours(0, 0, 0, 0);
    const diffTime = eventDate.getTime() - today.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const eventDuration = calculateEventDuration();
  const daysUntilEvent = calculateDaysUntilEvent();

  const toggleMultiDayEvent = () => {
    if (isMultiDayEvent) {
      onChange('event_end_date', null);
    } else if (formData.event_date) {
      onChange('event_end_date', formData.event_date);
    }
  };

  const isManualStatus = formData.campaign_status === 'Consultation' || formData.campaign_status === 'In Progress';
  const isAutoStatus = formData.campaign_status === 'Billing' || formData.campaign_status === 'Completed';
  const isActive = formData.campaign_status === 'In Progress';

  const canActivate = formData.client &&
    formData.brand &&
    formData.event_date &&
    formData.sponsorship_amount > 0;

  const handleToggle = (toActive: boolean) => {
    if (statusSaving || isAutoStatus) return;

    if (toActive) {
      if (!canActivate) return;
      onStatusChange?.('In Progress');
    } else {
      onStatusChange?.('Consultation');
    }
  };

  const getToggleValidationMessage = () => {
    if (!formData.client) return t('sponsorship.validation.sponsorRequired');
    if (!formData.brand) return t('sponsorship.validation.eventNameRequired');
    if (!formData.event_date) return t('sponsorship.validation.eventDateRequired');
    if (formData.sponsorship_amount <= 0) return t('sponsorship.validation.amountRequired');
    return '';
  };

  return (
    <div className="p-5 sm:p-6 space-y-6">
      {/* Status toggle block */}
      {isEditing && (
        <FormBlock title={t('sponsorship.statusBlock')}>
          <div>
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                {isManualStatus ? (
                  <div className="relative">
                    <div
                      className={`flex items-center bg-gray-100 rounded-full p-1 ${statusSaving ? 'opacity-50' : ''}`}
                      role="radiogroup"
                      aria-label={t('sponsorship.statusToggle')}
                    >
                      <button
                        type="button"
                        role="radio"
                        aria-checked={!isActive}
                        onClick={() => handleToggle(false)}
                        disabled={statusSaving}
                        className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                          !isActive
                            ? 'bg-amber-500 text-white shadow-sm'
                            : 'text-gray-600 hover:text-gray-800'
                        }`}
                      >
                        {t('sponsorship.status.consultation')}
                      </button>
                      <button
                        type="button"
                        role="radio"
                        aria-checked={isActive}
                        onClick={() => handleToggle(true)}
                        disabled={statusSaving || !canActivate}
                        title={!canActivate ? getToggleValidationMessage() : ''}
                        className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                          isActive
                            ? 'bg-emerald-500 text-white shadow-sm'
                            : canActivate
                              ? 'text-gray-600 hover:text-gray-800'
                              : 'text-gray-400 cursor-not-allowed'
                        }`}
                      >
                        {t('sponsorship.status.active')}
                      </button>
                    </div>
                    {statusSaving && (
                      <RefreshCw className="absolute -right-6 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-gray-500" />
                    )}
                  </div>
                ) : (
                  <div className={`px-4 py-2 rounded-lg font-semibold ${getStatusStyles(formData.campaign_status).combined}`}>
                    {ts(formData.campaign_status)}
                  </div>
                )}

                {isAutoStatus && (
                  <div className="flex items-center gap-1.5 text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                    <Zap className="w-3 h-3" />
                    <span>{t('sponsorship.statusAutomatic')}</span>
                  </div>
                )}

                {daysUntilEvent !== null && daysUntilEvent > 0 && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Clock className="w-4 h-4" />
                    <span>
                      {daysUntilEvent === 1
                        ? t('sponsorship.eventTomorrow')
                        : t('sponsorship.daysUntilEvent').replace('{days}', daysUntilEvent.toString())}
                    </span>
                  </div>
                )}

                {daysUntilEvent !== null && daysUntilEvent === 0 && (
                  <div className="flex items-center gap-2 text-sm text-accent-600 font-medium">
                    <Calendar className="w-4 h-4" />
                    <span>{t('sponsorship.eventToday')}</span>
                  </div>
                )}

                {daysUntilEvent !== null && daysUntilEvent < 0 && (
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <Calendar className="w-4 h-4" />
                    <span>{t('sponsorship.eventPassed')}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </FormBlock>
      )}

      {/* Base info block */}
      <FormBlock title={t('sponsorship.baseInfo')}>
        <div>
          <div className="cm-grid grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-4">
            <div className="cm-field">
              <label className="cm-label">{t('sponsorship.agent')}</label>
              <div className="cm-readonly flex items-center gap-2">
                <User className="w-4 h-4 text-gray-500" />
                <span>{agentName}</span>
              </div>
            </div>

            <div className="cm-field">
              <label className="cm-label">
                {t('sponsorship.year')} <span className="cm-req">*</span>
              </label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
                <select
                  name="year"
                  value={formData.year}
                  onChange={(e) => onChange('year', parseInt(e.target.value))}
                  required
                  className="cm-select pl-9"
                >
                  {years.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="cm-field">
              <label className="cm-label">
                {t('form.campaign.workCode')} <span className="cm-req">*</span>
              </label>
              <input
                type="text"
                name="work_code"
                data-required-field="sponsorship-work-code"
                value={formData.work_code}
                onChange={handleChange}
                required
                disabled={!isEditing}
                placeholder={isEditing ? '' : t('sponsorship.generating')}
                className="cm-input"
              />
              {!isEditing && (
                <p className="cm-help">{t('form.workCodeAutoGenerated')}</p>
              )}
            </div>
          </div>
        </div>
      </FormBlock>

      {/* Event details block */}
      <FormBlock title={t('sponsorship.eventDetails')}>
        {eventDuration && eventDuration > 1 && (
          <div className="flex justify-end mb-3">
            <div className="flex items-center gap-2 px-3 py-1 bg-teal-50 rounded-full text-sm font-medium text-teal-700">
              <CalendarRange className="w-4 h-4" />
              {t('sponsorship.eventDuration').replace('{days}', eventDuration.toString())}
            </div>
          </div>
        )}
        <div>
          <div className="cm-grid grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
            <div className="cm-field">
              <label className="cm-label">
                {t('sponsorship.eventName')} <span className="cm-req">*</span>
              </label>
              <input
                type="text"
                name="brand"
                data-required-field="sponsorship-brand"
                value={formData.brand}
                onChange={handleChange}
                required
                placeholder={t('sponsorship.eventNamePlaceholder')}
                className="cm-input"
              />
            </div>

            <div className="cm-field">
              <label className="cm-label">
                {t('sponsorship.sponsor')} <span className="cm-req">*</span>
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  name="client"
                  list="clients-list"
                  data-required-field="sponsorship-client"
                  value={formData.client}
                  onChange={handleChange}
                  required
                  placeholder={t('sponsorship.sponsorPlaceholder')}
                  className="cm-input flex-1"
                />
                <datalist id="clients-list">
                  {clients.map((client) => (
                    <option key={client} value={client} />
                  ))}
                </datalist>
              </div>
            </div>

            <div className="cm-field md:col-span-2">
              <div className="flex items-center gap-4 mb-3">
                <label className="cm-label">
                  {isMultiDayEvent ? t('sponsorship.eventStartDate') : t('sponsorship.eventDate')} <span className="cm-req">*</span>
                </label>
                <button
                  type="button"
                  onClick={toggleMultiDayEvent}
                  className={`flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium transition-all ${
                    isMultiDayEvent
                      ? 'bg-teal-100 text-teal-700'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <CalendarRange className="w-3 h-3" />
                  {t('sponsorship.multiDayEvent')}
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                <input
                  type="date"
                  name="event_date"
                  data-required-field="sponsorship-event-date"
                  value={formData.event_date || ''}
                  onChange={(e) => {
                    handleChange(e);
                    if (isMultiDayEvent && formData.event_end_date && e.target.value > formData.event_end_date) {
                      onChange('event_end_date', e.target.value);
                    }
                  }}
                  required
                  className="cm-date"
                />

                {isMultiDayEvent && (
                  <div className="cm-field">
                    <input
                      type="date"
                      name="event_end_date"
                      value={formData.event_end_date || ''}
                      onChange={handleChange}
                      min={formData.event_date || undefined}
                      className="cm-date"
                    />
                    <p className="cm-help">{t('sponsorship.endDateLabel')}</p>
                  </div>
                )}
              </div>
            </div>

            <div className="cm-field">
              <label className="cm-label">{t('sponsorship.campaignRef')}</label>
              <input
                type="text"
                name="campaign_ref"
                value={formData.campaign_ref}
                onChange={handleChange}
                placeholder={t('sponsorship.campaignRefPlaceholder')}
                className="cm-input"
              />
            </div>
          </div>
        </div>
      </FormBlock>

      {/* Financials block */}
      <FormBlock title={t('sponsorship.financials')}>
        <div>
          <div className="cm-grid grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-4 mb-6">
            <div className="cm-field">
              <label className="cm-label">
                {t('sponsorship.sponsorshipFee')} <span className="cm-req">*</span>
              </label>
              <div className="cm-affix has-left">
                <span>€</span>
                <input
                  type="number"
                  name="sponsorship_amount"
                  data-required-field="sponsorship-amount"
                  value={formData.sponsorship_amount || ''}
                  onChange={handleChange}
                  step="0.01"
                  min="0"
                  required
                  placeholder="0.00"
                  className="cm-input cm-numeric"
                />
              </div>
              <p className="cm-help">{t('sponsorship.sponsorshipFeeHelp')}</p>
            </div>

            <div className="cm-field">
              <label className="cm-label">
                {t('sponsorship.agencyCommission')} <span className="cm-req">*</span>
              </label>
              <div className="cm-affix has-right">
                <input
                  type="number"
                  name="agency_commission_percent"
                  value={commissionDisplay || ''}
                  onChange={(e) => {
                    const percent = parseFloat(e.target.value) || 0;
                    onChange('agency_commission_percent', percent / 100);
                  }}
                  step="1"
                  min="0"
                  max="100"
                  required
                  placeholder="20"
                  className="cm-input cm-numeric"
                />
                <span>%</span>
              </div>
              <p className="cm-help">{t('sponsorship.agencyCommissionHelp')}</p>
            </div>

            <div className="cm-field">
              <label className="cm-label">{t('sponsorship.profit')}</label>
              <div
                className={`cm-readonly flex items-center gap-2 font-semibold ${
                  profit > 0 ? 'cm-readonly--accent' : ''
                }`}
              >
                <TrendingUp className="w-4 h-4" />
                {formatCurrency(profit)}
              </div>
              <p className="cm-help">{t('sponsorship.profitFormula')}</p>
            </div>
          </div>

          <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Calculator className="w-5 h-5 text-teal-600 mt-0.5" />
              <div>
                <h4 className="font-semibold text-teal-900 mb-2">{t('sponsorship.financialSummary')}</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-teal-700">{t('sponsorship.sponsorshipFee')}:</span>
                    <span className="font-medium">{formatCurrency(formData.sponsorship_amount || 0)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-teal-700">{t('sponsorship.agencyCommission')} ({commissionDisplay}%):</span>
                    <span className="font-medium text-accent-600">+{formatCurrency(profit)}</span>
                  </div>
                  <div className="flex justify-between border-t border-teal-300 pt-2 mt-2">
                    <span className="font-bold text-teal-900">{t('sponsorship.agencyProfit')}:</span>
                    <span className={`font-bold text-lg ${profit > 0 ? 'text-accent-700' : 'text-gray-700'}`}>
                      {formatCurrency(profit)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </FormBlock>

      {/* Entitlements block */}
      <FormBlock title={t('sponsorship.entitlements')}>
        <div>
          <div className="cm-field">
            <label className="cm-label">{t('sponsorship.entitlementsLabel')}</label>
            <textarea
              name="entitlements"
              value={formData.entitlements || ''}
              onChange={handleChange}
              rows={6}
              placeholder={t('sponsorship.entitlementsPlaceholder')}
              className="cm-input resize-none"
            />
            <p className="cm-help">{t('sponsorship.entitlementsHelp')}</p>
          </div>
        </div>
      </FormBlock>
    </div>
  );
}
